/**
 * Menu definition. `roles` on a group decides who sees the section; `roles` on
 * an item narrows a single link inside it. The backend enforces the same rules
 * on every endpoint - the menu is a convenience, never a control.
 *
 * TWO THINGS TO KNOW BEFORE EDITING THIS FILE.
 *
 * 1. An administrator sees EVERY group at once. Anything repeated across
 *    groups - Tasks, Messages, Pipeline, a dashboard per category - therefore
 *    appeared four times in their sidebar and once in everybody else's. So:
 *      - the genuinely shared screens (Tasks, Messages) live in their own
 *        "personal" group, declared once;
 *      - anything that would still collide is removed by dedupe() below.
 *
 * 2. The four category dashboards are NOT four screens. /telepro, /commercial
 *    and /agency all render the same DashboardPage, which for an administrator
 *    returns the same company-wide payload - so an admin gets one, at /admin.
 *    /it is the exception: it is a different screen, about delivery, which is
 *    why an admin keeps it under a label that says so.
 */

/**
 * Only the administrator can fold a category away.
 *
 * They are the one who sees every section at once, so folding the ones they are
 * not working in keeps the menu on screen. Everybody else has a short, focused
 * menu where a fold button would only be a way to hide their own tools.
 */
export const FOLDABLE_ROLES = Object.freeze(['ADMIN']);

const ALL_ROLES = ['TELEPRO', 'COMMERCIAL', 'AGENCY', 'IT', 'ADMIN'];

export const NAVIGATION = [
  {
    // First, because it is the section an administrator actually works in -
    // and because dedupe() keeps the FIRST occurrence of a repeated label.
    key: 'admin',
    labelKey: 'nav.admin',
    roles: ['ADMIN'],
    // An administrator LOOKS. They do not call a prospect, do not fill in a
    // qualification form and own no customers of their own, so none of the
    // doing screens are here - the three sales categories below are no longer
    // shown to them at all. What is here is the whole book, the money, and the
    // levers: reassign a rep, staff an agency, chase an unpaid invoice.
    items: [
      { to: '/admin', labelKey: 'nav.dashboard', end: true },
      { to: '/admin/customers', labelKey: 'nav.customersOverview' },
      { to: '/admin/pipeline', labelKey: 'nav.pipeline' },
      { to: '/documents', labelKey: 'nav.documents' },
      { to: '/admin/receivables', labelKey: 'nav.receivables' },
      { to: '/admin/stats', labelKey: 'nav.stats' },
      { to: '/admin/products', labelKey: 'nav.products' },
    ],
  },
  {
    // Who works here, and where. Kept apart from the numbers because staffing
    // is a different job from reading the book.
    key: 'teams',
    labelKey: 'nav.teams',
    roles: ['ADMIN'],
    items: [
      { to: '/admin/users', labelKey: 'nav.users' },
      { to: '/admin/agencies', labelKey: 'nav.agencies' },
    ],
  },
  {
    key: 'teleprospection',
    labelKey: 'nav.teleprospection',
    // Not the administrator's: every screen in here is an act of prospecting.
    // Their view of the same accounts is /admin/customers.
    roles: ['TELEPRO'],
    // No "book a meeting" entry: a RDV is the closing step of a qualification,
    // reached from the form, not an errand you start from the menu.
    items: [
      { to: '/telepro', labelKey: 'nav.dashboard', end: true, roles: ['TELEPRO'] },
      // The menu follows the call itself: a prospect is a company nobody has
      // called yet, so it comes first - qualifying one turns it into a lead.
      { to: '/telepro/prospects', labelKey: 'nav.myProspects' },
      { to: '/telepro/qualification', labelKey: 'nav.qualification' },
      { to: '/telepro/leads', labelKey: 'nav.myLeads' },
      { to: '/telepro/pipeline', labelKey: 'nav.pipeline' },
      { to: '/telepro/agenda', labelKey: 'nav.myAgenda' },
    ],
  },
  {
    key: 'commercial',
    labelKey: 'nav.commercial',
    // "My customers", "my diary" - an administrator has neither.
    roles: ['COMMERCIAL'],
    items: [
      { to: '/commercial', labelKey: 'nav.dashboard', end: true, roles: ['COMMERCIAL'] },
      { to: '/commercial/pipeline', labelKey: 'nav.pipeline' },
      { to: '/commercial/customers', labelKey: 'nav.myCustomers' },
      { to: '/commercial/calendar', labelKey: 'nav.calendar' },
      // A rep quotes the deal they are closing, so quotes and invoices belong in
      // the sales menu - scoped by the API to their own customers' paperwork.
      { to: '/documents', labelKey: 'nav.documents' },
    ],
  },
  {
    // Its own section rather than a link inside Commercial: what you configure
    // about yourself is not part of the day's work. Working hours sit here
    // because they drive the calendar teleprospection books into.
    key: 'availability',
    labelKey: 'nav.settings',
    roles: ['COMMERCIAL'],
    items: [{ to: '/commercial/availability', labelKey: 'nav.availability' }],
  },
  {
    key: 'agency',
    labelKey: 'nav.agency',
    roles: ['AGENCY'],
    items: [
      { to: '/agency', labelKey: 'nav.dashboard', end: true, roles: ['AGENCY'] },
      { to: '/agency/customers', labelKey: 'nav.agencyCustomers' },
      { to: '/agency/pipeline', labelKey: 'nav.pipeline' },
      { to: '/agency/calendar', labelKey: 'nav.calendar' },
      { to: '/agency/team', labelKey: 'nav.team' },
      { to: '/agency/stats', labelKey: 'nav.stats' },
    ],
  },
  {
    key: 'it',
    labelKey: 'nav.it',
    roles: ['IT', 'ADMIN'],
    // The delivery work, and nothing else: system health and the connector
    // registry are platform administration and live in the admin section.
    items: [
      { to: '/it', labelKey: 'nav.dashboard', end: true, roles: ['IT'] },
      // The same screen, named for what it is when an administrator - who
      // already has a dashboard of their own - looks at it.
      { to: '/it', labelKey: 'nav.deliveryDashboard', end: true, roles: ['ADMIN'] },
      { to: '/it/projects', labelKey: 'nav.projects' },
      // The signed accounts, which for an administrator is a subset of the
      // customer table they already have.
      { to: '/it/customers', labelKey: 'nav.allCustomers', roles: ['IT'] },
    ],
  },
  {
    key: 'system',
    labelKey: 'nav.system',
    roles: ['ADMIN'],
    items: [
      { to: '/admin/settings', labelKey: 'nav.settings' },
      { to: '/admin/translations', labelKey: 'nav.translations' },
      { to: '/admin/audit', labelKey: 'nav.auditLog' },
      { to: '/admin/health', labelKey: 'nav.systemHealth' },
      { to: '/admin/integrations', labelKey: 'nav.integrations' },
    ],
  },
  {
    // Cross-cutting, so declared once instead of copied into every category.
    key: 'personal',
    labelKey: 'nav.personal',
    roles: ALL_ROLES,
    items: [
      { to: '/tasks', labelKey: 'nav.tasks' },
      { to: '/chat', labelKey: 'nav.chat' },
    ],
  },
];

/**
 * The same screen must not appear twice in one sidebar.
 *
 * Keyed on the LABEL rather than the URL, because the duplicates that matter
 * are the ones a person reads as the same thing: /telepro/pipeline and
 * /commercial/pipeline are two routes rendering one board, and an administrator
 * seeing "Pipeline" three times learns nothing from the second and the third.
 *
 * First occurrence wins, which is why the admin section is declared first.
 */
const dedupe = (groups) => {
  const seen = new Set();
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (seen.has(item.labelKey)) return false;
        seen.add(item.labelKey);
        return true;
      }),
    }))
    .filter((group) => group.items.length > 0);
};

export const navigationForRole = (role) =>
  dedupe(
    NAVIGATION.filter((group) => group.roles.includes(role)).map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.roles || item.roles.includes(role)),
    })),
  );

/** Where a user lands right after login. */
export const homeForRole = (role) =>
  ({
    TELEPRO: '/telepro',
    COMMERCIAL: '/commercial',
    AGENCY: '/agency',
    IT: '/it',
    ADMIN: '/admin',
  })[role] ?? '/telepro';
